import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchTaskBoard,
  fetchTaskBoards,
  fetchTaskAssignees,
  fetchTask,
  createBoard,
  renameBoard,
  deleteBoard,
  createTask,
  updateTask,
  moveTask,
  addTaskComment,
  deleteTaskComment,
  deleteTask,
  createColumn,
  renameColumn,
  deleteColumn,
  getApiErrorMessage,
} from "../api";
import Loading from "../components/Loading";
import ErrorRetry from "../components/ErrorRetry";
import KanbanBoard from "../components/tasks/KanbanBoard";
import TaskModal from "../components/tasks/TaskModal";
import TaskFilters from "../components/tasks/TaskFilters";
import BoardSelector from "../components/tasks/BoardSelector";
import {
  buildVisibleTasks,
  countRootTasks,
  rootTasksForColumn,
  taskMatchesFilters,
} from "../components/tasks/taskFilterUtils";
import {
  isSystemColumn,
  readCollapsedColumnIds,
  toggleCollapsedColumnId,
  writeCollapsedColumnIds,
} from "../components/tasks/columnUtils";
import { stripCommentThread } from "../components/tasks/taskCommentUtils";
import { useAuth } from "../context/AuthContext";

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + "T00:00:00");
  return due < today;
}

function isTaskOverdue(task) {
  return !task.parent_task_id && !task.is_done && isOverdue(task.due_date);
}

export default function TasksBoard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBoardFromUrl = useRef(Number(searchParams.get("board")) || null);
  const [pendingTaskId, setPendingTaskId] = useState(() => {
    const id = Number(searchParams.get("task"));
    return id || null;
  });

  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [board, setBoard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [watcherOnly, setWatcherOnly] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  const [addingColumn, setAddingColumn] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState([]);
  const modalOpenRef = useRef(false);

  const loadBoards = useCallback(async () => {
    const data = await fetchTaskBoards();
    setBoards(data);
    return data;
  }, []);

  const loadBoard = useCallback(async (boardId) => {
    if (!boardId) return null;
    setError(null);
    try {
      const data = await fetchTaskBoard(boardId);
      setBoard(data.board);
      setColumns(data.columns);
      setTasks(data.tasks);
      return data;
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось загрузить доску задач"));
      return null;
    }
  }, []);

  const refreshEditingTask = useCallback(
    async (taskId) => {
      await loadBoard(selectedBoardId);
      if (!taskId) return;
      try {
        const full = await fetchTask(taskId);
        setEditingTask(full);
      } catch (err) {
        setError(getApiErrorMessage(err, "Не удалось обновить задачу"));
      }
    },
    [loadBoard, selectedBoardId]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([loadBoards(), fetchTaskAssignees().then(setEmployees)])
      .then(([boardsData]) => {
        if (cancelled) return;
        const urlBoardId = initialBoardFromUrl.current;
        if (urlBoardId && boardsData.some((b) => b.id === urlBoardId)) {
          setSelectedBoardId(urlBoardId);
        } else if (boardsData.length > 0) {
          const defaultBoard = boardsData.find((b) => b.is_default) || boardsData[0];
          setSelectedBoardId(defaultBoard.id);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, "Ошибка загрузки"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadBoards]);

  useEffect(() => {
    if (!selectedBoardId) return;
    loadBoard(selectedBoardId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get("board") === String(selectedBoardId)) return prev;
        next.set("board", String(selectedBoardId));
        return next;
      },
      { replace: true }
    );
  }, [selectedBoardId, loadBoard, setSearchParams]);

  useEffect(() => {
    setCollapsedIds(readCollapsedColumnIds(window.localStorage, selectedBoardId));
  }, [selectedBoardId]);

  useEffect(() => {
    if (!pendingTaskId || !selectedBoardId || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const full = await fetchTask(pendingTaskId);
        if (cancelled) return;
        if (full.board_id !== selectedBoardId) {
          setSelectedBoardId(full.board_id);
          return;
        }
        setEditingTask(full);
        setModalOpen(true);
        setPendingTaskId(null);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("task");
            return next;
          },
          { replace: true }
        );
      } catch (err) {
        if (!cancelled) {
          setError(getApiErrorMessage(err, "Не удалось открыть задачу из ссылки"));
          setPendingTaskId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingTaskId, selectedBoardId, loading, setSearchParams]);

  useEffect(() => {
    modalOpenRef.current = modalOpen;
  }, [modalOpen]);

  useEffect(() => {
    const onFocus = () => {
      if (modalOpenRef.current) return;
      if (selectedBoardId) loadBoard(selectedBoardId);
      loadBoards();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadBoard, loadBoards, selectedBoardId]);

  const filtersActive = Boolean(assigneeFilter || priorityFilter || overdueOnly || watcherOnly);

  const filterArgs = useMemo(
    () => ({
      assigneeFilter,
      priorityFilter,
      overdueOnly,
      watcherOnly,
      currentUserId: user?.id ?? null,
      isOverdue: isTaskOverdue,
    }),
    [assigneeFilter, priorityFilter, overdueOnly, watcherOnly, user?.id]
  );

  const matchingTaskIds = useMemo(() => {
    if (!filtersActive) return new Set();
    return new Set(
      tasks.filter((t) => taskMatchesFilters(t, filterArgs)).map((t) => t.id)
    );
  }, [tasks, filterArgs, filtersActive]);

  const visibleTasks = useMemo(
    () => buildVisibleTasks(tasks, filterArgs, filtersActive),
    [tasks, filterArgs, filtersActive]
  );

  const rootTasks = useMemo(
    () => visibleTasks.filter((t) => !t.parent_task_id),
    [visibleTasks]
  );

  const subtasksByParent = useMemo(() => {
    const map = {};
    for (const t of tasks) {
      if (t.parent_task_id) {
        (map[t.parent_task_id] = map[t.parent_task_id] || []).push(t);
      }
    }
    for (const list of Object.values(map)) {
      list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id);
    }
    return map;
  }, [tasks]);

  const columnsWithTasks = useMemo(() => {
    return columns.map((col) => {
      const allRootsInColumn = tasks.filter(
        (t) => t.column_id === col.id && !t.parent_task_id
      );
      const visibleRoots = rootTasksForColumn(rootTasks, col.id);
      return {
        ...col,
        tasks: visibleRoots,
        totalTaskCount: allRootsInColumn.length,
        isSystem: isSystemColumn(col),
      };
    });
  }, [columns, rootTasks, tasks]);

  const taskStats = useMemo(() => {
    const inProgressIds = new Set(
      columns.filter((c) => c.slug === "in_progress").map((c) => c.id)
    );
    return {
      rootCount: tasks.filter((t) => !t.parent_task_id).length,
      inProgressCount: tasks.filter(
        (t) => !t.parent_task_id && inProgressIds.has(t.column_id)
      ).length,
      overdueCount: tasks.filter((t) => isTaskOverdue(t)).length,
      filteredVisibleCount: countRootTasks(tasks, visibleTasks, filtersActive),
    };
  }, [tasks, visibleTasks, filtersActive, columns]);

  const handleBoardChange = (boardId) => setSelectedBoardId(boardId);

  const handleClearFilters = () => {
    setAssigneeFilter("");
    setPriorityFilter("");
    setOverdueOnly(false);
    setWatcherOnly(false);
  };

  const handleCreateBoard = async (data) => {
    setError(null);
    try {
      const newBoard = await createBoard(data);
      await loadBoards();
      return newBoard;
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось создать доску"));
      throw err;
    }
  };

  const handleRenameBoard = async (boardId, data) => {
    setError(null);
    try {
      await renameBoard(boardId, data);
      await loadBoards();
      await loadBoard(boardId);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось переименовать доску"));
      throw err;
    }
  };

  const handleDeleteBoard = async (boardId) => {
    setError(null);
    try {
      await deleteBoard(boardId);
      const remaining = await loadBoards();
      const next = remaining.find((b) => b.is_default) || remaining[0];
      setSelectedBoardId(next ? next.id : null);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось удалить доску"));
      throw err;
    }
  };

  const handleRenameColumn = async (columnId, data) => {
    setError(null);
    try {
      await renameColumn(columnId, data);
      await loadBoard(selectedBoardId);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось переименовать колонку"));
    }
  };

  const handleDeleteColumn = async (columnId, moveTo) => {
    setError(null);
    try {
      await deleteColumn(columnId, moveTo);
      await loadBoard(selectedBoardId);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось удалить колонку"));
      throw err;
    }
  };

  const handleToggleCollapsed = (columnId) => {
    setCollapsedIds((prev) => {
      const next = toggleCollapsedColumnId(prev, columnId);
      writeCollapsedColumnIds(window.localStorage, selectedBoardId, next);
      return next;
    });
  };

  const collapsedIdSet = useMemo(() => new Set(collapsedIds), [collapsedIds]);

  const handleMoveColumn = async (columnId, position) => {
    setError(null);
    const snapshot = columns;
    setColumns((prev) => {
      const from = prev.findIndex((col) => col.id === columnId);
      if (from < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(position, 0, moved);
      return next.map((col, idx) => ({ ...col, position: idx }));
    });
    try {
      await renameColumn(columnId, { position });
      await loadBoard(selectedBoardId);
    } catch (err) {
      setColumns(snapshot);
      setError(getApiErrorMessage(err, "Не удалось переместить колонку"));
    }
  };

  const handleAddColumn = async (nameFromPrompt) => {
    const name = (nameFromPrompt ?? newColumnName).trim();
    if (!name) {
      setAddingColumn(true);
      return;
    }
    setError(null);
    try {
      await createColumn(selectedBoardId, { name });
      setNewColumnName("");
      setAddingColumn(false);
      await loadBoard(selectedBoardId);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось создать колонку"));
    }
  };

  const handleOpenCreate = () => {
    setError(null);
    setEditingTask(null);
    setModalOpen(true);
  };

  const handleOpenEdit = async (task) => {
    setError(null);
    setEditingTask(task);
    setModalOpen(true);
    try {
      const full = await fetchTask(task.id);
      setEditingTask(full);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось загрузить задачу"));
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTask(null);
  };

  const handleSave = async (data) => {
    setError(null);
    try {
      await createTask({ ...data, board_id: selectedBoardId });
      await loadBoard(selectedBoardId);
      await loadBoards();
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось сохранить задачу"));
      throw err;
    }
  };

  const handleAutoSave = async (data) => {
    if (!editingTask) return;
    setError(null);
    try {
      const updated = await updateTask(editingTask.id, data);
      setEditingTask((prev) => ({
        ...prev,
        ...updated,
        comments: prev?.comments ?? updated.comments,
      }));
      setTasks((prev) =>
        prev.map((t) => {
          if (t.id === updated.id) {
            return { ...t, ...updated, comments: t.comments };
          }
          if (data.column_id != null && t.parent_task_id === updated.id) {
            return {
              ...t,
              column_id: updated.column_id,
              is_done: updated.is_done,
              completed_at: updated.completed_at,
            };
          }
          return t;
        })
      );
      if (updated.board_id !== selectedBoardId) {
        setSelectedBoardId(updated.board_id);
      }
      if (data.column_id != null || data.is_done != null) {
        await loadBoard(selectedBoardId);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось сохранить изменения"));
      throw err;
    }
  };

  const handleCreateSubtask = async (parentId, data) => {
    setError(null);
    try {
      await createTask({
        ...data,
        board_id: selectedBoardId,
        parent_task_id: parentId,
      });
      await refreshEditingTask(parentId);
      await loadBoards();
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось создать подзадачу"));
      throw err;
    }
  };

  const handleToggleSubtaskOnBoard = async (subtask) => {
    const next = !subtask.is_done;
    setError(null);
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id === subtask.id) {
          return { ...t, is_done: next };
        }
        if (t.id === subtask.parent_task_id) {
          const delta = next ? 1 : -1;
          const done = Math.max(0, (t.subtask_done_count || 0) + delta);
          return { ...t, subtask_done_count: done };
        }
        return t;
      })
    );
    try {
      await updateTask(subtask.id, { is_done: next });
      await loadBoards();
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось обновить подзадачу"));
      await loadBoard(selectedBoardId);
    }
  };

  const handleUpdateSubtask = async (subtaskId, data) => {
    setError(null);
    try {
      await updateTask(subtaskId, data);
      if (editingTask?.id) {
        await refreshEditingTask(editingTask.id);
      } else {
        await loadBoard(selectedBoardId);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось обновить подзадачу"));
      throw err;
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    const parentId = editingTask?.id;
    setError(null);
    try {
      await deleteTask(subtaskId);
      if (parentId) {
        await refreshEditingTask(parentId);
      } else {
        await loadBoard(selectedBoardId);
      }
      await loadBoards();
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось удалить подзадачу"));
      throw err;
    }
  };

  const handleDelete = async (taskId) => {
    setError(null);
    try {
      await deleteTask(taskId);
      await loadBoard(selectedBoardId);
      await loadBoards();
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось удалить задачу"));
      throw err;
    }
  };

  const handleAddComment = async (taskId, data) => {
    setError(null);
    try {
      const comment = await addTaskComment(taskId, data);
      setEditingTask((prev) =>
        prev?.id === taskId
          ? { ...prev, comments: [...(prev.comments || []), comment] }
          : prev
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось добавить комментарий"));
      throw err;
    }
  };

  const handleDeleteComment = async (taskId, commentId) => {
    setError(null);
    try {
      await deleteTaskComment(taskId, commentId);
      setEditingTask((prev) =>
        prev?.id === taskId
          ? { ...prev, comments: stripCommentThread(prev.comments, commentId) }
          : prev
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось удалить комментарий"));
      throw err;
    }
  };

  const handleMoveTask = async (taskId, columnId, position) => {
    setError(null);
    const snapshot = tasks;
    const doneCol = columns.find((c) => c.slug === "done");
    const movingToDone = doneCol && columnId === doneCol.id;
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id === taskId) {
          return {
            ...t,
            column_id: columnId,
            position,
            is_done: Boolean(movingToDone),
            completed_at: movingToDone
              ? t.completed_at || new Date().toISOString()
              : null,
          };
        }
        if (t.parent_task_id === taskId) {
          return {
            ...t,
            column_id: columnId,
            is_done: Boolean(movingToDone),
            completed_at: movingToDone
              ? t.completed_at || new Date().toISOString()
              : null,
          };
        }
        return t;
      })
    );
    try {
      await moveTask(taskId, { column_id: columnId, position });
      await loadBoard(selectedBoardId);
    } catch (err) {
      setTasks(snapshot);
      setError(getApiErrorMessage(err, "Не удалось переместить задачу"));
    }
  };

  const defaultColumnId = columns.length > 0 ? columns[0].id : null;

  const modalSubtasks = editingTask
    ? tasks
        .filter((t) => t.parent_task_id === editingTask.id)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id)
    : [];

  if (loading) return <Loading text="Загрузка доски задач..." />;
  if (error && !board) {
    return (
      <ErrorRetry
        message={error}
        onRetry={() => {
          setLoading(true);
          loadBoards()
            .then((boardsData) => {
              const defaultBoard = boardsData.find((b) => b.is_default) || boardsData[0];
              if (defaultBoard) setSelectedBoardId(defaultBoard.id);
            })
            .catch((err) => setError(getApiErrorMessage(err, "Ошибка загрузки")))
            .finally(() => setLoading(false));
        }}
      />
    );
  }

  return (
    <div className="tasks-page">
      <div className="section-header">
        <div>
          <h2>{board?.name || "Задачи"}</h2>
          <p className="section-subtitle">Внутренние задачи компании · Kanban-доска</p>
        </div>
        <div className="controls">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleOpenCreate}>
            Создать задачу
          </button>
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => loadBoard(selectedBoardId)}
            title="Обновить"
            aria-label="Обновить доску"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </div>
      </div>

      <div className="tasks-toolbar data-card">
        <BoardSelector
          boards={boards}
          selectedBoardId={selectedBoardId}
          onChange={handleBoardChange}
          onCreateBoard={handleCreateBoard}
          onRenameBoard={handleRenameBoard}
          onDeleteBoard={handleDeleteBoard}
        />

        <TaskFilters
          employees={employees}
          assigneeFilter={assigneeFilter}
          priorityFilter={priorityFilter}
          overdueOnly={overdueOnly}
          watcherOnly={watcherOnly}
          onAssigneeChange={setAssigneeFilter}
          onPriorityChange={setPriorityFilter}
          onOverdueChange={setOverdueOnly}
          onWatcherOnlyChange={setWatcherOnly}
          onClear={handleClearFilters}
          filtersActive={filtersActive}
        />
      </div>

      {error && (
        <div className="error task-inline-error" role="alert">
          {error}
          <button type="button" className="task-alert-action" onClick={() => setError(null)}>
            Скрыть
          </button>
        </div>
      )}

      {filtersActive && (
        <div className="task-alert" role="status">
          <span>Фильтры активны — перетаскивание отключено.</span>
          <button type="button" className="task-alert-action" onClick={handleClearFilters}>
            Сбросить
          </button>
        </div>
      )}

      <div className="tasks-stats">
        <div className="tasks-stat">
          <span className="tasks-stat-value">{taskStats.rootCount}</span>
          <span className="tasks-stat-label">Задач</span>
        </div>
        <div className="tasks-stat tasks-stat-progress">
          <span className="tasks-stat-value">{taskStats.inProgressCount}</span>
          <span className="tasks-stat-label">В работе</span>
        </div>
        {taskStats.overdueCount > 0 ? (
          <button
            type="button"
            className={`tasks-stat tasks-stat-warn tasks-stat-clickable ${overdueOnly ? "tasks-stat-active" : ""}`}
            onClick={() => setOverdueOnly((v) => !v)}
            aria-pressed={overdueOnly}
            title={overdueOnly ? "Показать все задачи" : "Показать только просроченные"}
          >
            <span className="tasks-stat-value">{taskStats.overdueCount}</span>
            <span className="tasks-stat-label">Просрочено</span>
          </button>
        ) : (
          <div className="tasks-stat">
            <span className="tasks-stat-value">{taskStats.overdueCount}</span>
            <span className="tasks-stat-label">Просрочено</span>
          </div>
        )}
        <div className="tasks-stat tasks-stat-accent">
          <span className="tasks-stat-value">{taskStats.filteredVisibleCount}</span>
          <span className="tasks-stat-label">{filtersActive ? "На доске" : "Всего"}</span>
        </div>
      </div>

      {columnsWithTasks.length > 0 ? (
        <KanbanBoard
          columns={columnsWithTasks}
          rootTasks={rootTasks}
          subtasksByParent={subtasksByParent}
          onToggleSubtask={handleToggleSubtaskOnBoard}
          onTaskClick={handleOpenEdit}
          onMoveTask={handleMoveTask}
          dragDisabled={filtersActive}
          matchingTaskIds={matchingTaskIds}
          onRenameColumn={handleRenameColumn}
          onDeleteColumn={handleDeleteColumn}
          onMoveColumn={handleMoveColumn}
          onToggleCollapsed={handleToggleCollapsed}
          collapsedIds={collapsedIdSet}
          onAddColumn={() => handleAddColumn()}
          addingColumn={addingColumn}
          newColumnName={newColumnName}
          onNewColumnNameChange={setNewColumnName}
          onCancelAddColumn={() => {
            setAddingColumn(false);
            setNewColumnName("");
          }}
          onConfirmAddColumn={() => handleAddColumn(newColumnName)}
        />
      ) : (
        <div className="empty-state kanban-empty-state">
          <span>Доска не настроена</span>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => handleAddColumn()}>
            + Добавить колонку
          </button>
        </div>
      )}

      <TaskModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onAutoSave={handleAutoSave}
        onDelete={handleDelete}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
        onCreateSubtask={handleCreateSubtask}
        onUpdateSubtask={handleUpdateSubtask}
        onDeleteSubtask={handleDeleteSubtask}
        task={editingTask}
        subtasks={modalSubtasks}
        employees={employees}
        columns={columns}
        boards={boards}
        defaultColumnId={defaultColumnId}
      />
    </div>
  );
}
